import React, { useState, useEffect } from 'react';
import {
    Box, Typography, TextField, Button, CircularProgress, Modal, Snackbar, Alert
} from '@mui/material';
import { motion } from 'framer-motion';
import { BASE_URL } from '../../utils/endpoints';

// Animation variants for modal content
const modalVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: "easeOut" }
    },
    exit: {
        opacity: 0,
        scale: 0.9,
        transition: { duration: 0.3, ease: "easeIn" }
    }
};

// Animation variants for form fields
const fieldVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: {
        opacity: 1,
        x: 0,
        transition: { duration: 0.4, ease: "easeOut" }
    }
};

// Animation variants for buttons
const buttonVariants = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: {
        opacity: 1,
        scale: 1,
        transition: { duration: 0.4, ease: "easeOut" }
    },
    hover: {
        scale: 1.05,
        transition: { duration: 0.2 }
    }
};

const CategoryEdit = ({ open, handleClose, category, onCategoryUpdated }) => {
    // States for category data and form
    const [categoryName, setCategoryName] = useState('');
    const [newCategoryName, setNewCategoryName] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    // Populate form fields when category prop changes
    useEffect(() => {
        if (category) {
            setCategoryName(category.name || '');
            setNewCategoryName(category.name || '');
        }
    }, [category]);

    // Handle PUT request for updating the category
    const handleUpdateCategory = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError(null);
        setOpenSnackbar(false);

        if (!newCategoryName?.trim()) {
            setError('Category name cannot be empty');
            setSnackbarMessage('Category name cannot be empty');
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${BASE_URL}/categories/${category?.id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: newCategoryName,
                }),
            });

            if (response.ok) {
                const updatedCategory = await response.json();
                setCategoryName(updatedCategory.name);
                setSnackbarMessage(`Category "${newCategoryName}" updated successfully!`);
                setSnackbarSeverity('success');
                setOpenSnackbar(true);
                onCategoryUpdated(); // Notify parent to refresh the list
                setTimeout(() => {
                    handleClose(); // Close the modal after success
                }, 1000);
            } else {
                const errorMessage = 'Failed to update category';
                setError(errorMessage);
                setSnackbarMessage(errorMessage);
                setSnackbarSeverity('error');
                setOpenSnackbar(true);
            }
        } catch (err) {
            const errorMessage = 'Error occurred while updating category';
            setError(errorMessage);
            setSnackbarMessage(errorMessage);
            setSnackbarSeverity('error');
            setOpenSnackbar(true);
        } finally {
            setLoading(false);
        }
    };

    // Handle Snackbar close
    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    return (
        <Modal
            open={open}
            onClose={handleClose}
            aria-labelledby="edit-category-modal-title"
            aria-describedby="edit-category-modal-description"
        >
            <motion.div
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
            >
                <Box
                    sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: 650,
                        maxHeight: '80vh',
                        overflowY: 'auto',
                        bgcolor: 'background.paper',
                        boxShadow: 24,
                        p: 3,
                        borderRadius: 2,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                    }}
                >
                    <Typography
                        id="edit-category-modal-title"
                        variant="h6"
                        component="h2"
                        sx={{ color: '#2e7d32', fontWeight: "bold" }}
                    >
                        Edit Category
                    </Typography>

                    <Box
                        component="form"
                        onSubmit={handleUpdateCategory}
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            width: '100%',
                        }}
                    >
                        <motion.div variants={fieldVariants}>
                            <TextField
                                label="Category Name"
                                value={newCategoryName}
                                onChange={(e) => setNewCategoryName(e.target.value)}
                                fullWidth
                                variant="outlined"
                                required
                                disabled={loading}
                                InputLabelProps={{
                                    style: { color: '#2e7d32' },
                                    sx: {
                                        '&.Mui-focused': {
                                            color: '#2e7d32',
                                        },
                                    },
                                }}
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': { borderColor: '#2e7d32' },
                                        '&:hover fieldset': { borderColor: '#2e7d32' },
                                        '&.Mui-focused fieldset': { borderColor: '#2e7d32' },
                                    },
                                }}
                            />
                        </motion.div>

                        {error && (
                            <motion.div variants={fieldVariants}>
                                <Typography color="error" variant="body2">
                                    {error}
                                </Typography>
                            </motion.div>
                        )}

                        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end', mt: 2 }}>
                            <motion.div variants={buttonVariants} whileHover="hover">
                                <Button
                                    variant="outlined"
                                    onClick={handleClose}
                                    disabled={loading}
                                    sx={{ minWidth: 100, color: '#2e7d32', borderColor: '#2e7d32' }}
                                >
                                    Cancel
                                </Button>
                            </motion.div>
                            <motion.div variants={buttonVariants} whileHover="hover">
                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={loading}
                                    sx={{ minWidth: 100, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#45a049' } }}
                                >
                                    {loading ? <CircularProgress size={24} /> : 'Update Category'}
                                </Button>
                            </motion.div>
                        </Box>
                    </Box>

                    <Snackbar
                        open={openSnackbar}
                        autoHideDuration={3000}
                        onClose={handleCloseSnackbar}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={handleCloseSnackbar}
                            severity={snackbarSeverity}
                            sx={{ width: '100%' }}
                        >
                            {snackbarMessage}
                        </Alert>
                    </Snackbar>
                </Box>
            </motion.div>
        </Modal>
    );
};

export default CategoryEdit;